/**
 * @file OverviewContactSection.tsx
 * @description «Связь» на «Обзоре» клиента (phase19.31, SC68): последнее
 * сообщение диалога, «Написать» и можно ли слать рекламу.
 *
 * Переписки не было (`contact: null`) — только строка про согласие на
 * рекламу. «Написать» ведёт туда же, куда шапка (`customerChatHref` — место
 * замены на окно чата SC64).
 */

import { MessageSquare } from "lucide-react";
import { Link } from "react-router";

import { Button } from "../../ui/button";
import { SectionCard } from "../../ui/SectionCard";
import type { CustomerOverviewDto } from "../../../lib/api-customers-overview";
import { pluralRu } from "../../../lib/plural";
import { formatShopDateTime } from "../../../lib/shop-time";
import { marketingText } from "./overview-view";

interface Props {
  contact: CustomerOverviewDto["contact"];
  marketingConsent: CustomerOverviewDto["marketingConsent"];
  /** Адрес переписки; нет — кнопки «Написать» нет (нет права на переписку). */
  chatHref?: string;
}

export function OverviewContactSection({ contact, marketingConsent, chatHref }: Props) {
  return (
    <SectionCard title="Связь">
      {contact ? (
        <div className="flex flex-col gap-1">
          <p className="type-body break-words text-on-surface">
            {contact.lastMessagePreview ? `«${contact.lastMessagePreview}»` : "Сообщений пока нет"}
          </p>
          <p className="type-caption text-on-surface-variant">
            {[
              contact.lastMessageFromCustomer ? "пишет клиент" : "ответили мы",
              contact.lastMessageAt ? formatShopDateTime(contact.lastMessageAt) : null,
              contact.unreadCount > 0
                ? `${contact.unreadCount} ${pluralRu(contact.unreadCount, ["непрочитанное", "непрочитанных", "непрочитанных"])}`
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {contact && chatHref ? (
          <Button variant="outline" asChild>
            <Link to={chatHref}>
              <MessageSquare className="size-4" aria-hidden />
              Написать
            </Link>
          </Button>
        ) : null}
        <p className="type-body text-on-surface-variant">{marketingText(marketingConsent)}</p>
      </div>
    </SectionCard>
  );
}
